import React, { useState } from 'react';
import { Avatar, Button, Col, Modal, Row, Tooltip, Switch } from 'antd';
import _ from 'lodash';
import { isReliableAndExitsOrManuallyApproved, isReliableAndNotOnDocument } from '../../lib/util/status';
import { generateUUID, getLabelValue, getResultInfo } from '../../lib/util';
import useGlobal from '../../store/Store';
import { Label } from '../../types/label';
import { SectionTitle, SectionSubTitle } from '../../components/text/Titles';
import { Icon } from '@ant-design/compatible';
import { useTranslation } from 'react-i18next';
import { AnnotationFieldResult } from './AnnotationFieldResult';

const ButtonGroup = Button.Group;

type Props = {
  onAccept: (label: Label) => any;
  onDelete: (label: Label) => any;
  onEdit: (label: Label) => any;
  datasetMode: boolean;
  beautifyLabels?: boolean | true;
  showShortcuts?: boolean;
  inTable: boolean;
  publicDemo?: boolean;
};

export const Results = ({
  onAccept,
  onDelete,
  onEdit,
  datasetMode,
  beautifyLabels,
  showShortcuts,
  inTable,
  publicDemo,
}: Props) => {
  const { t, i18n } = useTranslation();
  const [hoveredLabel, setHoveredLabel] = useState<null | Label>(null);
  const [globalState, globalActions] = useGlobal();

  const onMouseEnter = (label: Label) => {
    setHoveredLabel(label);
    globalActions.setLabelSelected(label);
  };

  const onMouseLeave = () => {
    setHoveredLabel(null);
    globalActions.setLabelSelected(null);
  };

  const onCopyLabel = async (copiedLabel: Label) => {
    const label: Label = {
      id: generateUUID(),
      name: copiedLabel.name,
      source: null,
      page: null,
      word: null,
      exists: false,
      reliable: true,
      color: copiedLabel.color,
      manuallyApproved: false,
      location: null,
    };

    globalActions.addLabel(label);
    onEdit(label);
    globalActions.setLabelEditCopied(true);
  };

  const showInfoModal = () => {
    Modal.info({
      title: 'Results info',
      content: (
        <div style={{ marginTop: '20px' }}>
          <p>
            {getLabelAvatar({ exists: true, reliable: true })}
            <span style={{ marginLeft: '20px' }}>{t(`label.${getResultInfo({ exists: true, reliable: true })}`)}</span>
          </p>
          <p>
            {getLabelAvatar({ exists: false, reliable: false })}
            <span style={{ marginLeft: '20px' }}>
              {t(`label.${getResultInfo({ exists: false, reliable: false })}`)}
            </span>
          </p>
          <p>
            {getLabelAvatar({ exists: false, reliable: true })}
            <span style={{ marginLeft: '20px' }}>{t(`label.${getResultInfo({ exists: false, reliable: true })}`)}</span>
          </p>
        </div>
      ),
    });
  };

  const handleNewValue = (label: Label, newValue: string) => {
    label.word = newValue;
    onAccept(label);
  };

  const getResult = (label: Label, firstOfLabelName: boolean) => {
    return (
      <Row
        key={label.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '10px 0px',
          borderBottom: '1px solid #e8e8e8',
          backgroundColor: hoveredLabel && label.id === hoveredLabel.id ? '#e6f7ff' : 'transparent',
        }}
        onMouseEnter={() => !isReliableAndNotOnDocument(label) && onMouseEnter(label)}
        onMouseLeave={() => !isReliableAndNotOnDocument(label) && onMouseLeave()}>
        <Col span={4} style={{ padding: '0px 20px' }}>
          <Tooltip title={t(`label.${getResultInfo(label)}`)}>{getLabelAvatar(label)}</Tooltip>
        </Col>
        <Col span={globalState.readOnly ? 20 : 8} style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 14, color: '#494a50' }}>
            {beautifyLabels
              ? getLabelValue(label.name)
              : i18n.exists(`label.${label.name}`) && !datasetMode
              ? t(`label.${label.name}`)
              : label.name}
          </span>
          {!isReliableAndNotOnDocument(label) && (
            <span style={{ fontSize: 14, color: '#999EA6', wordWrap: 'break-word' }}>
              <AnnotationFieldResult
                initialValue={label.word || ''}
                handleNewValue={newValue => handleNewValue(label, newValue)}
              />
            </span>
          )}
        </Col>
        {!globalState.readOnly && (
          <Col span={11} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Col span={3} offset={1}>
              {label.shortcut && firstOfLabelName && showShortcuts && (
                <span
                  style={{
                    width: '30px',
                    display: 'inline-block',
                    padding: '8px 0',
                    textAlign: 'center',
                    backgroundColor: '#efefef',
                    borderRadius: '4px',
                  }}>
                  {label.shortcut}
                </span>
              )}
            </Col>
            <Col span={18} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ButtonGroup>
                {datasetMode ? null : (
                  <Button
                    onClick={() => onAccept(label)}
                    disabled={isReliableAndExitsOrManuallyApproved(label) || isReliableAndNotOnDocument(label)}>
                    <Icon type="check" />
                  </Button>
                )}
                <Button
                  onClick={() => onEdit(label)}
                  type={globalState.labelEdit && globalState.labelEdit.id === label.id ? 'primary' : 'default'}>
                  <Icon type="edit" />
                </Button>
                <Button
                  onClick={() => onCopyLabel(label)}
                  disabled={
                    isReliableAndNotOnDocument(label) ||
                    globalState.labels.filter(l => l.name === label.name && l.exists === false).length > 0
                  }>
                  <Icon type="copy" />
                </Button>
                <Button onClick={() => onDelete(label)} disabled={isReliableAndNotOnDocument(label)}>
                  <Icon type="delete" />
                </Button>
              </ButtonGroup>
            </Col>
          </Col>
        )}
      </Row>
    );
  };

  const getLabelAvatar = (label: Partial<Label>) => {
    return (
      <Avatar
        className={
          !isReliableAndNotOnDocument(label) && hoveredLabel && label.id === hoveredLabel.id ? 'connectLineB' : ''
        }
        shape="square"
        icon={
          <Icon
            type={isReliableAndExitsOrManuallyApproved(label) || isReliableAndNotOnDocument(label) ? 'check' : 'minus'}
            style={{
              color: publicDemo
                ? '#fff'
                : label.color && isReliableAndExitsOrManuallyApproved(label)
                ? '#fff'
                : isReliableAndExitsOrManuallyApproved(label)
                ? '#52c41a'
                : isReliableAndNotOnDocument(label)
                ? '#8c8c8c'
                : '#fa541c',
              display: 'inline-block',
              verticalAlign: 'middle',
            }}
          />
        }
        style={{
          backgroundColor: publicDemo
            ? '#00b9ff'
            : label.color && datasetMode && isReliableAndExitsOrManuallyApproved(label)
            ? label.color
            : isReliableAndExitsOrManuallyApproved(label)
            ? '#d9f7be'
            : isReliableAndNotOnDocument(label)
            ? '#e8e8e8'
            : '#ffccc7',
          cursor: 'pointer',
          opacity: label.color && isReliableAndExitsOrManuallyApproved(label) ? 0.7 : 1,
        }}
      />
    );
  };

  return (
    <div>
      {!inTable && (
        <Row className="px-5">
          <Col span={20}>
            <SectionTitle>Results</SectionTitle>
            <br />
            {!datasetMode && (
              <SectionSubTitle>
                Show all labels: <Switch onChange={checked => globalActions.setAllLabels(checked)} />
              </SectionSubTitle>
            )}
          </Col>
          <Col
            span={4}
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '7px',
            }}>
            <Icon type="info-circle" onClick={showInfoModal} />
          </Col>
        </Row>
      )}
      {_.sortBy(
        globalState.labels.filter(label => label && !label.hidden).filter(label => !label.isTable),
        it => it.name.toLowerCase()
      ).map((label, i, array) => {
        const firstOfLabelName = array[i - 1]?.name !== label.name;
        const selectableLabel = globalState.labelOptions?.find(l => l.name === label.name);
        const labelWithExtraProps = { ...label, color: selectableLabel?.color, shortcut: selectableLabel?.shortcut };
        return getResult(labelWithExtraProps, firstOfLabelName);
      })}
    </div>
  );
};

export default Results;
